import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink, LogOut } from 'lucide-react';
import { useWallet } from '../context/WalletContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { isConnected, address, formatAddress, openModal, disconnectWallet } = useWallet();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
  }, [location]);

  const navLinks = [
    {
      name: 'Ecosystem',
      dropdown: [
        { name: 'Dobix Wallet', path: '/wallet' },
        { name: 'Staking', href: '#' },
        { name: 'Trading Bot', href: '#' },
        { name: 'Dobix Pay', href: '#' },
      ]
    },
    {
      name: 'Updates',
      dropdown: [
        { name: 'Roadmap', path: '/roadmap' },
        { name: 'Blog', href: '#' },
        { name: 'Announcements', href: '#' },
      ]
    },
    {
      name: 'Community',
      dropdown: [
        { name: 'Twitter / X', href: 'https://twitter.com', external: true },
        { name: 'Discord', href: 'https://discord.com', external: true },
        { name: 'Telegram', href: 'https://telegram.org', external: true },
      ]
    },
    { name: 'About', path: '/about' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/30 backdrop-blur-xl border-b border-white/5' : 'bg-black/20 backdrop-blur-lg'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/dobix-logo.png" alt="Dobix" className="h-8 w-8 lg:h-10 lg:w-10" />
            <span className="text-lg lg:text-xl font-bold text-white font-heading">Dobix</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative group"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.path ? (
                  <Link
                    to={link.path}
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center ${isActive(link.path) ? 'text-[#FF6B00]' : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <button className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white flex items-center">
                    {link.name}
                    <ChevronDown className="ml-1 w-4 h-4" />
                  </button>
                )}

                {/* Desktop Dropdown */}
                {link.dropdown && activeDropdown === link.name && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-[#111] border border-[#222] rounded-lg shadow-xl overflow-hidden">
                    {link.dropdown.map((item, idx) => (
                      item.path ? (
                        <Link key={idx} to={item.path} className="block px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-[#1a1a1a]">
                          {item.name}
                        </Link>
                      ) : (
                        <a key={idx} href={item.href} target={item.external ? '_blank' : undefined} rel={item.external ? 'noopener noreferrer' : undefined} className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-[#1a1a1a]">
                          {item.name}
                          {item.external && <ExternalLink className="w-3 h-3" />}
                        </a>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <>
                {/* Connected state - Desktop */}
                <div className="hidden lg:flex items-center space-x-3">
                  <span className="text-sm text-gray-400">{formatAddress(address)}</span>
                  <button
                    onClick={disconnectWallet}
                    className="text-sm text-gray-400 hover:text-red-400 transition-colors"
                  >
                    Disconnect
                  </button>
                </div>

                {/* Connected state - Mobile: Small elegant disconnect button */}
                <div className="lg:hidden flex items-center space-x-2">
                  <div className="flex items-center bg-[#111] border border-[#222] rounded-full px-3 py-1.5">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2 animate-pulse" />
                    <span className="text-xs text-gray-300 font-medium">{formatAddress(address)}</span>
                  </div>
                  <button
                    onClick={disconnectWallet}
                    className="p-2 bg-[#111] border border-[#222] rounded-full hover:border-red-500/50 hover:bg-red-500/10 transition-all group"
                    title="Disconnect Wallet"
                  >
                    <LogOut className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-400 transition-colors" />
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Desktop Buy Button */}
                <button
                  onClick={openModal}
                  className="hidden lg:block bg-[#FF6B00]/80 hover:bg-[#FF6B00] text-white font-medium px-4 py-1.5 rounded-lg text-sm transition-all backdrop-blur-sm"
                >
                  Buy Dobix
                </button>

                {/* Mobile Buy Button */}
                <button
                  onClick={openModal}
                  className="lg:hidden bg-[#FF6B00]/80 hover:bg-[#FF6B00] text-white font-medium px-3 py-1.5 rounded-lg text-xs transition-all backdrop-blur-sm"
                >
                  Buy
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;